import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import { useRouter } from '@core/utils/utils'
import { ref } from '@vue/composition-api'
import i18n from '@/libs/i18n'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useCommon from '@/views/organization/useCommon'
import useCommonLibrary from '../useCommonLibrary'

export default function useLibraryEdit() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { route, router } = useRouter()
  const { getItem } = useLambda()
  const { updateDocumentMetadata, collection, sendMetadataEditedEmail } = useCommonLibrary()
  const { formatDate } = useCommon()
  const userData = store.state?.userStore?.userData
  const document = ref({})
  const originalDocument = ref({}) // Copia sin transformar para detección de cambios
  const isSubmitting = ref(false)

  async function show() {
    isSubmitting.value = true

    try {
      const query = {
        _id: oid(route.value.params.id),
        client_id: oid(userData.client.$oid),
        deleted: { $ne: true },
      }

      const item = await getItem({ collection, query })

      if (!item) {
        showErrorMessage(i18n.t('message.you_are_not_authorized'))
        router.push({ name: 'organization-library-list' })
        return
      }

      // Inicializar history si no existe (documentos legacy con auditLog)
      if (!item.history) {
        item.history = item.auditLog || [] // Migrar de auditLog si existe, o inicializar vacío
      }

      // Guardar copia SIN transformar para detección de cambios (ObjectIds puros)
      originalDocument.value = JSON.parse(JSON.stringify(item))

      // Transformar para el formulario
      document.value = {
        ...item,
        responsable: item.responsable?.toString() || null,
        clientes: item.clientes?.map(c => c.toString()) || [],
        fechaProximaRevision: item.fechaProximaRevision ? new Date(item.fechaProximaRevision).toISOString().split('T')[0] : null,
      }
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('biblioteca.error_fetch'))
    } finally {
      isSubmitting.value = false
    }
  }

  async function update(data) {
    isSubmitting.value = true

    try {
      // Incluir TODOS los campos para detección correcta de cambios
      const payload = {
        nombre: data.nombre?.value || '',
        tipoDocumento: data.tipoDocumento?.value || '',
        tematica: data.tematica?.value || '',
        responsable: data.responsable?.value ? oid(data.responsable.value) : null,
        clientes: data.clientes?.value ? data.clientes.value.map(c => oid(c)) : [],
        observaciones: data.observaciones?.value || '',
        fechaProximaRevision: data.fechaProximaRevision?.value ? date(formatDate(data.fechaProximaRevision.value)) : null,
      }

      const previousResponsable = document.value.responsable
      const newResponsable = data.responsable?.value

      // Pasar copia profunda para evitar mutaciones de Vue refs
      const originalDataCopy = JSON.parse(JSON.stringify(originalDocument.value))
      await updateDocumentMetadata(route.value.params.id, payload, originalDataCopy)

      // Send email notification
      if (newResponsable) {
        const responsableChanged = previousResponsable !== newResponsable
        sendMetadataEditedEmail(route.value.params.id, {
          nombre: data.nombre.value,
          tipoDocumento: data.tipoDocumento.value,
          versionActual: document.value.versionActual,
          estado: document.value.estado,
          fechaProximaRevision: data.fechaProximaRevision.value,
        }, newResponsable, responsableChanged)
      }

      showSuccessMessage(i18n.t('biblioteca.updated_success'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('biblioteca.error_update'))
    } finally {
      router.push({ name: 'organization-library-list' })
      isSubmitting.value = false
    }
  }

  return {
    update,
    show,
    document,
    isSubmitting,
  }
}
